import React, { useState, useEffect } from "react";
import { Form, InputNumber, Button, Card, Typography, Row, Col } from "antd";

const { Title, Text } = Typography;

const MortgageAffordabilityCalculator = () => {
  const [maxLoanAmount, setMaxLoanAmount] = useState(null);
  const [currency, setCurrency] = useState("$");

  const formatNumber = (value) => {
    if (!value) return "";
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const parseNumber = (value) => {
    return value ? value.replace(/\s/g, "") : "";
  };

  useEffect(() => {
        fetch("https://ipapi.co/json/")
          .then((res) => res.json())
          .then((data) => {
            const countryCode = data.country_code; // e.g., ZA, US
            setCurrency(getCurrencyCode(countryCode));
          })
          .catch((err) => {
            console.error("Error fetching currency:", err);
            setCurrency("$"); // Fallback
          });
      }, []);
    
      const getCurrencyCode = (countryCode) => {
        const currencyMap = {
          ZA: "R",   // South Africa
          US: "$",   // United States
          GB: "£",   // United Kingdom
          NG: "₦",   // Nigeria
          KE: "KES", // Kenya
          IN: "₹",   // India
        };
        return currencyMap[countryCode] || "$";
      };

  const calculateAffordability = (values) => {
    const { income, expenses, savings, interestRate, loanTerm } = values;
    
    const I = income;
    const E = expenses + savings; // Including savings as an extra expense
    const r = interestRate / 100 / 12; // Monthly interest rate
    const n = loanTerm * 12; // Loan term in months
    
    if (I > E && r > 0 && n > 0) {
      const P = (I - E) / (r * (1 - Math.pow(1 + r, -n)));
      setMaxLoanAmount(formatNumber(P.toFixed(2)));
    } else {
      setMaxLoanAmount(null);
    }
  };

  return (
    <Card title="Mortgage Affordability Calculator" style={{ maxWidth: 800, margin: "auto", marginTop: 20 }}>
      <Row gutter={24}>
        <Col span={15}>
          <Form layout="vertical" onFinish={calculateAffordability} initialValues={{ income: 30000, expenses: 10000, savings: 0, interestRate: 11, loanTerm: 20 }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={"Monthly Net Income (" + currency + ")"} name="income" rules={[{ required: true, message: "Please enter your monthly income" }]}> 
                  <InputNumber style={{ width: "100%" }} min={1} step={1000} formatter={formatNumber} parser={parseNumber} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={"Expenses (Excl. Rent)"} name="expenses" rules={[{ required: true, message: "Please enter your monthly expenses (excluding rent)" }]}> 
                  <InputNumber style={{ width: "100%" }} min={0} step={1000} formatter={formatNumber} parser={parseNumber} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={"Monthly Savings (" + currency + ")"} name="savings" rules={[{ required: true, message: "Please enter your monthly savings amount" }]}> 
                  <InputNumber style={{ width: "100%" }} min={0} step={1000} formatter={formatNumber} parser={parseNumber} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Annual Interest Rate (%)" name="interestRate" rules={[{ required: true, message: "Please enter the interest rate" }]}> 
                  <InputNumber style={{ width: "100%" }} min={0.1} step={0.1} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Loan Term (Years)" name="loanTerm" rules={[{ required: true, message: "Please enter the loan term" }]}> 
                  <InputNumber style={{ width: "100%" }} min={1} max={30} step={1} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Calculate Affordability
              </Button>
            </Form.Item>
          </Form>
        </Col>
        <Col span={9}>
          {maxLoanAmount !== null && (
            <Card style={{ textAlign: "center" }}>
              <Title level={4}>Maximum Loan</Title>
              <Text strong style={{ fontSize: "18px" }}>
                {currency} {maxLoanAmount}
              </Text>
            </Card>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default MortgageAffordabilityCalculator;
